import {
  Container,
  Box,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  Tooltip,
  makeStyles,
} from "@material-ui/core";
// Remove the duplicate import
import React, {
  FC,
  useContext,
  useEffect,
  useState,
  useCallback,
  memo,
} from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IdentityApplicationViewState } from "libs/resources/identity/applications/application-views/IdentityApplicationInfo";
import { useAsyncAction } from "legacy-components/form/action/AsyncAction";
import { EnvironmentType } from "header/environment-selector/ENV_CONSTANTS";
import { useSelector } from "react-redux";
import Alert from "@material-ui/lab/Alert";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from "@material-ui/icons/Close";
import Submit from "modus/components/form/submit/Submit";
import { SnackNotificationContext } from "legacy-components/notifications/GenericSnackNotification";
import { mapToArray } from "libs/utils/shared/common";
import _ from "lodash";
import ApplicationService from "pages/home/applications/services/ApplicationService";
import CallbackLogoutField from "pages/home/applications/components/CallbackLogoutField";
import { CSSTransition } from "react-transition-group";
import { isGrantTypeEnabled } from "pages/home/apis/components/grant-types/GrantTypeForm";
import { useAuthorization } from "pages/home/authorizer/components/AuthorizationProvider";
import { useParams } from "react-router";
import { validateCallbackLogoutUrls } from "libs/utils/urlValidation";

interface GrantTypeProps {
  value: string;
  title: string;
  helperText: string;
}

const FrontEndSupportedGrantType = "authorization_code";
export const useGrantTypeDetails = () => {
    const intl = useIntl();
   
    return ([
        {
            value: "authorization_code",
            title: intl.formatMessage({
                defaultMessage: "Authorization Code Grant"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "The Authorization Code Grant allows confidential and public clients to exchange an authorization code for an access token via a trusted back channel."
            }),
            type: "primary"
        },
        {
            value: "client_credentials",
            title: intl.formatMessage({
                defaultMessage: "Client Credentials Grant"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "The Client Credentials Grant allows clients to obtain an access token outside of the context of a user with a secured environments."
            }),
            type: "primary"
        },
        {
            value: "urn:ietf:params:oauth:grant-type:device_code",
            title: intl.formatMessage({
                defaultMessage: "Device Code"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "The Device Code grant type allows input-constrained or no-browser devices to exchange a previously obtained code for an access token."
            }),
            type: "primary"
        },
        // TODO: This Grant type is needed for legacy applications if want to enable..
        /*{
            value: "implicit",
            title: intl.formatMessage({
                defaultMessage: "Implicit Grant"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "The Implicit Grant allows clients to obtain an access token without an intermediate code exchange step, rather than being returned via a trusted back channel like in the Authorization Code Grant."
            })
        },*/
        {
            value: "refresh_token",
            title: intl.formatMessage({
                defaultMessage: "Use Refresh Tokens"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "A refresh token allows an application to obtain a new access token without prompting the user for specified amount of time."
            }),
            type: "secondary"
        },
        {
            value: "urn:ietf:params:oauth:grant-type:token-exchange",
            title: intl.formatMessage({
                defaultMessage: "Token Exchange"
            }),
            helperText: intl.formatMessage({
                defaultMessage: "Sharing of identity and security information in heterogeneous environments or across security domains."
            }),
            type: "secondary"
        }]);
}

interface GrantTypesFormProps {
  onGrantTypeUpdate: (grantTypes: string[]) => void;
  onLogoutUriChange: (logoutUri: string[]) => void;
  onRedirectUriChange: (redirectUri: string[]) => void;
}

const useGrantTypeUpdate = (
  formValues: any,
  updatedGrantTypes: any,
  applicationName: string
) => {
  const identityEnvironment = useSelector(
    (state: any) => state?.common?.identityEnvironment
  );
  const [updateState, { subscribe }] = useAsyncAction(() =>
    ApplicationService.updateApplication(
      identityEnvironment as EnvironmentType,
      //state?.application?.applicationName as string,
      applicationName,
      {
        grantTypes: updatedGrantTypes,
        logoutUri: makeUrls(formValues.logout),
        redirectUri: makeUrls(formValues.callback),
      }
    )
  );

  const makeUrls = useCallback(
    (urlString?: string) =>
      urlString
        ?.split(",")
        .map((value) => String(value).trim())
        .filter((value) => value.length > 0) ?? [],
    []
  );

  return { updateState, subscribe };
};
const useStyles = makeStyles(() => ({
  dynamicContent: {
    position: "relative",
    marginTop: "20px",
    "&.expand-enter": {
      opacity: 0,
      transform: "translateY(-20px)",
    },
    "&.expand-enter-active": {
      opacity: 1,
      transform: "translateY(0)",
      transition: "opacity 300ms, transform 300ms",
    },
    "&.expand-exit": {
      opacity: 1,
      transform: "translateY(0)",
    },
    "&.expand-exit-active": {
      opacity: 0,
      transform: "translateY(-20px)",
      transition: "opacity 300ms, transform 300ms",
    },
  },
}));

export const GrantTypesAppCreationForm: FC<GrantTypesFormProps> = memo(
  ({ onGrantTypeUpdate, onLogoutUriChange, onRedirectUriChange }) => {
    const classes = useStyles();
    const authenticationMethods = useGrantTypeDetails();
    const [updatedGrantTypes, setUpdatedGrantTypes] = useState<string[]>([]);

    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
    const onChange = (method: any) => {
      let currentGrantTypes;
        if (updatedGrantTypes.includes(method.value)) {
            currentGrantTypes  = updatedGrantTypes.filter((value) => value !== method.value);
        } else {
            currentGrantTypes = [...updatedGrantTypes , method.value];
        }
        // if no primary types selcted, then remove all secondary types
        if (!currentGrantTypes.includes("authorization_code") && !currentGrantTypes.includes("client_credentials")&& !currentGrantTypes.includes("urn:ietf:params:oauth:grant-type:device_code") &&
            (!currentGrantTypes.includes("refresh_token") || !currentGrantTypes.includes("urn:ietf:params:oauth:grant-type:token-exchange"))
        ) {
            currentGrantTypes = (currentGrantTypes.filter((value) => value !== "refresh_token" && value !== "urn:ietf:params:oauth:grant-type:token-exchange"));
        }

        setUpdatedGrantTypes(currentGrantTypes);
 
    };
    useEffect(() => {
      if (!updatedGrantTypes) {
        return;
      }
      onGrantTypeUpdate(updatedGrantTypes);
    }, [updatedGrantTypes]);

    const identityEnvironment = useSelector(
      (state: any) => state?.common?.identityEnvironment
    );
    const notification = useContext(SnackNotificationContext);

    const [formValues, setFormValues] = React.useState({
      callback: "",

      logout: "",
    });

    const [AlertClose, setAlertClose] = React.useState({
      info: false,
      error: true,
    });

    const callback_length = formValues.callback
      ?.split(",")
      .map((str) => str.trim())
      .filter((str) => str.length > 0).length;

    const logout_length = formValues.logout
      ?.split(",")
      .map((str) => str.trim())
      .filter((str) => str.length > 0).length;

    const [counter, setCounter] = React.useState({
      callback: callback_length === undefined ? 0 : callback_length,
      logout: formValues.logout === undefined ? 0 : logout_length,
    });

    const [error, setError] = React.useState({
      callback: { value: false },
      logout: { value: false },
    });

    const handleChange = (e: any) => {
      const { name, value } = e.target;

      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
    };

    useEffect(() => {
      if (!formValues.callback) {
        return;
      }
      const callback_length = formValues.callback
        ?.split(",")
        .map((str) => str.trim())
        .filter((str) => str.length > 0).length;

      setCounter({
        callback: callback_length === undefined ? 0 : callback_length,
        logout: counter.logout,
      });
      setError({
        callback: { value: validateCallbackLogoutUrls(formValues.callback) !== "" },
        logout: { value: validateCallbackLogoutUrls(formValues.logout) !== "" },
      });

      if (validateCallbackLogoutUrls(formValues.callback) === "") {
        onRedirectUriChange(
          formValues.callback?.split(",").map((str) => str.trim())
        );
      } else {
        onRedirectUriChange([]);
      }
    }, [formValues.callback]);

    useEffect(() => {
      if (!formValues.logout) {
        return;
      }
      const logout_length = formValues.logout
        ?.split(",")
        .map((str) => str.trim())
        .filter((str) => str.length > 0).length;

      setCounter({
        callback: counter.callback,
        logout: logout_length === undefined ? 0 : logout_length,
      });
      setError({
        callback: { value: validateCallbackLogoutUrls(formValues.callback) !== "" },
        logout: { value: validateCallbackLogoutUrls(formValues.logout) !== "" },
      });
      if (validateCallbackLogoutUrls(formValues.logout) === "") {
        onLogoutUriChange(
          formValues.logout?.split(",").map((str) => str.trim())
        );
      } else {
        onLogoutUriChange([]);
      }
    }, [formValues.logout]);

    useEffect(() => {
      const validate = () => {
        if (error.callback.value || error.logout.value) {
          setSubmitDisabled(true);
          return;
        }
        setSubmitDisabled(true);
      };
      validate();
    }, [
      updatedGrantTypes,
      formValues.logout,
      formValues.callback,
      identityEnvironment,
      error,
    ]);

    return (
      <>
        <Grid item sm={12} md={6} style={{ order: 2 }}>
          <Box>
            <Paper>
              <Box padding={2}>
                <Typography
                  variant="h6"
                  style={{ fontSize: "14px", fontWeight: 700 }}
                >
                  Grant Types
                </Typography>
                <List
                  style={{
                    borderBottom: `1px solid #6A6E79`,
                    paddingBottom: "20px",
                  }}
                >
                  {authenticationMethods.map((method, index) => (
                    <ListItem
                      key={index}
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                      <ListItemIcon>
                        <Checkbox
                            color='primary' 
                            checked={updatedGrantTypes.includes(method.value)}
                            onChange={() => onChange(method)}
                            style={{padding: "7px"}}
                            disabled={isGrantTypeEnabled(method, updatedGrantTypes, authenticationMethods, true )? false : true}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              columnGap: "10px",
                              alignItems: "self-end",
                            }}
                          >
                            {method.title}
                            {method.value === "authorization_code" && (
                              <Tooltip
                                title={
                                  <span>
                                    Use Authorization Code Grant to set up a
                                    user sign in screen for your application.
                                  </span>
                                }
                                placement="right"
                              >
                                <InfoOutlinedIcon color={"primary"} />
                              </Tooltip>
                            )}
                          </Box>
                        }
                        // secondary={method.helperText}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </Box>
        </Grid>
        <Grid item md={12} style={{ order: 4 }}>
          <Box>
            <CSSTransition
              in={updatedGrantTypes.includes("authorization_code")}
              timeout={300}
              classNames="expand"
              unmountOnExit
            >
              <Box style={{ backgroundColor: "white" }}>
                <Container maxWidth={false} style={{ padding: "20px 20px" }}>
                  <Typography
                    variant="h6"
                    style={{ fontSize: "14px", fontWeight: 700 }}
                  >
                    Callback and Logout URLs
                  </Typography>
                  <form noValidate autoComplete="off">
                    <Box style={{ paddingRight: "15px" }}>
                      {!AlertClose.info && (
                        <Alert
                          variant="outlined"
                          severity="info"
                          style={{
                            color: "#0063A3",
                            borderColor: "#0063A3",
                            borderLeft: "12px solid #0063A3",
                            margin: "16px 0px",
                          }}
                          action={
                            <CloseIcon
                              onClick={() => {
                                setAlertClose({
                                  info: true,
                                  error: AlertClose.error,
                                });
                              }}
                              style={{ color: "#0063A3", width: "16px" }}
                            />
                          }
                          icon={
                            <InfoOutlinedIcon style={{ color: "#0063A3" }} />
                          }
                        >
                          <Typography style={{ fontWeight: 700 }}>
                            You are allowed to register a total of 250 callback
                            URLs and 250 logout URLs.
                          </Typography>
                        </Alert>
                      )}
                      <Container
                        maxWidth={false}
                        style={{ padding: "0px", marginTop: "20px" }}
                      >
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={12} md={6}>
                            <CallbackLogoutField
                              counter={counter.callback}
                              label={"Allowed Callback URLs"}
                              placeholder={
                                "Add comma separated URLs to be allowed"
                              }
                              value={formValues.callback}
                              name={"callback"}
                              onChange={handleChange}
                              setError={() => {}}
                              customError={
                                validateCallbackLogoutUrls(formValues.callback) === ""
                                  ? { valid: false, message: "" }
                                  : {
                                      valid: true,
                                      message: validateCallbackLogoutUrls(
                                        formValues.callback
                                      ),
                                    }
                              }
                            />
                          </Grid>
                          <Grid item xs={12} sm={12} md={6}>
                            <CallbackLogoutField
                              counter={counter.logout}
                              label={"Allowed Logout URLs"}
                              placeholder={
                                "Add comma separated URLs to be allowed"
                              }
                              value={formValues.logout}
                              name={"logout"}
                              onChange={handleChange}
                              setError={() => {}}
                              customError={
                                validateCallbackLogoutUrls(formValues.logout) === ""
                                  ? { valid: false, message: "" }
                                  : {
                                      valid: true,
                                      message: validateCallbackLogoutUrls(
                                        formValues.logout
                                      ),
                                    }
                              }
                            />
                          </Grid>
                        </Grid>
                      </Container>
                    </Box>
                  </form>
                </Container>
              </Box>
            </CSSTransition>
          </Box>
        </Grid>
      </>
    );
  }
);

export default GrantTypesAppCreationForm;
